// Repayment vectors behind each customer's projected fundings, as cumulative percent
// of the funding repaid by the end of each repayment month (month 1 = the first
// month money comes back). After the last month of a curve, its last monthly step
// repeats, the same as the calculation.
//
// Non-live and future customers (customer-service pipelineIncrementalFractions):
//   their own vector, or the generic curve when Generic is checked. Points are
//   cumulative ROAS % by period (period 0 = repayment month 1). A missing period
//   carries the previous value, and a fall counts as zero.
// Live customers (deal-service NewCohortMonthReturn):
//   funding x incrementalRoas[month - 1] x revenueMargin, and, when the deal's
//   marketing view drives repayment, x total / view spend (1 / view ratio for
//   projected months), as the asset tape's revenue share does.

import { customerStage } from './pipelineExposure';

export const REPAYMENT_VECTOR_SOURCES = {
  own: 'Own vector',
  generic: 'Generic curve',
  cohorts: 'From sales cohorts',
  fallback: 'Fallback 0.2 a month (no sales cohorts)',
};

function round1(value) {
  return Math.round(value * 10) / 10;
}

// pipelineIncrements mirrors customer-service pipelineIncrementalFractions, in percent points.
export function pipelineIncrements(points) {
  const cumulative = new Map();
  let maxMonth = -1;
  (points || []).forEach((point) => {
    const month = Number(point?.month);
    if (!Number.isInteger(month) || month < 0) return;
    cumulative.set(month, Number(point.cumulativeRoasPct) || 0);
    if (month > maxMonth) maxMonth = month;
  });
  const out = [];
  let previous = 0;
  for (let month = 0; month <= maxMonth; month += 1) {
    const current = cumulative.has(month) ? cumulative.get(month) : previous;
    out.push(Math.max(0, current - previous));
    previous = current;
  }
  return out;
}

// cumulativeFromIncrements adds the monthly steps, repeating the last one past the end.
export function cumulativeFromIncrements(increments, months) {
  const out = [];
  if (!increments.length) return out;
  let total = 0;
  for (let index = 0; index < months; index += 1) {
    total += increments[Math.min(index, increments.length - 1)];
    out.push(round1(total));
  }
  return out;
}

function pipelineVectorRow(client, settings) {
  const generic = !!client.useGenericCurve;
  const points = generic ? settings?.genericVector : client.repaymentVector;
  const increments = pipelineIncrements(points);
  return {
    source: generic ? 'generic' : 'own',
    increments,
    curveLength: increments.length,
  };
}

// viewRepaymentFactor is total / view spend for projected months: 1 / ratio when
// the deal's marketing view drives repayment, else 1.
export function viewRepaymentFactor(marketingView) {
  const ratio = Number(marketingView?.ratio);
  if (!marketingView?.forRepayment || !(ratio > 0)) return 1;
  return 1 / ratio;
}

function liveVectorRow(vector, marketingView) {
  if (!vector || !Array.isArray(vector.incrementalRoas) || !vector.incrementalRoas.length) return null;
  const margin = Number(vector.revenueMargin) > 0 ? Number(vector.revenueMargin) : 1;
  const viewFactor = viewRepaymentFactor(marketingView);
  const increments = vector.incrementalRoas.map((value) => (Number(value) || 0) * margin * viewFactor * 100);
  return {
    source: vector.source === 'fallback' ? 'fallback' : 'cohorts',
    increments,
    curveLength: increments.length,
    margin,
    viewFactor,
  };
}

// repaymentVectorModel builds one row per customer with projected fundings. Columns
// run to the longest curve (at least 12, at most maxMonths).
export function repaymentVectorModel(customers, pipelineClients, settings, maxMonths = 36) {
  const clientsById = new Map((pipelineClients || []).map((client) => [client.customerId, client]));
  const rows = [];
  const liveWithout = [];
  (customers || []).forEach((customer) => {
    const stage = customerStage(customer, pipelineClients);
    const name = customer.companyName || customer.customerId;
    const client = clientsById.get(customer.customerId);
    let vector = null;
    if (stage === 'Live') {
      vector = liveVectorRow(customer.repaymentVector, customer.marketingView);
      if (!vector) {
        liveWithout.push(name);
        return;
      }
    } else if (client) {
      vector = pipelineVectorRow(client, settings);
    }
    if (!vector) return;
    rows.push({ customerId: customer.customerId, name, stage, ...vector });
  });
  const longest = rows.reduce((max, row) => Math.max(max, row.curveLength), 0);
  const months = Math.min(maxMonths, Math.max(12, longest));
  return {
    months,
    rows: rows.map((row) => ({ ...row, cumulative: cumulativeFromIncrements(row.increments, months) })),
    liveWithout,
  };
}
